import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

/**
 * Ad slot for Basic (free) tier users only.
 *
 * Tier rules:
 *  - subscription_tier === 'premium' or 'ultimate' → renders nothing (ad-free)
 *  - all others (free / logged-out) → shows ad subject to frequency cap
 *
 * Frequency cap: max 3 impressions per 25 minutes per device (localStorage).
 *
 * Networks (drop in zone IDs via .env when your account is approved):
 *  REACT_APP_EXOCLICK_BANNER_ZONE   → 320x50 / 728x90 sticky banner zone id
 *  REACT_APP_EXOCLICK_NATIVE_ZONE   → native ad zone id
 *
 * Variants:
 *  'native-card' — fits inside the character grid as a single card
 *  'sticky'      — sticky bottom banner with dismiss button
 *  'banner'      — inline full-width banner
 */

const STORAGE_KEY = 'animus_ad_impressions';
const CAP = 3;
const WINDOW_MS = 25 * 60 * 1000; // 25 minutes

function pruneImpressions() {
  try {
    const raw = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
    const now = Date.now();
    const fresh = raw.filter((t) => now - t < WINDOW_MS);
    if (fresh.length !== raw.length) localStorage.setItem(STORAGE_KEY, JSON.stringify(fresh));
    return fresh;
  } catch {
    return [];
  }
}

function canShowAd() {
  return pruneImpressions().length < CAP;
}

function registerImpression() {
  try {
    const fresh = pruneImpressions();
    fresh.push(Date.now());
    localStorage.setItem(STORAGE_KEY, JSON.stringify(fresh));
  } catch {
    /* ignore */
  }
}

function isAdFreeTier(tier) {
  return tier === 'premium' || tier === 'ultimate';
}

const EXOCLICK_BANNER_ZONE = process.env.REACT_APP_EXOCLICK_BANNER_ZONE || '';
const EXOCLICK_NATIVE_ZONE = process.env.REACT_APP_EXOCLICK_NATIVE_ZONE || '';

function ExoClickIframe({ zoneId, width, height, testId }) {
  // ExoClick serves via a documented iframe URL: https://syndication.exoclick.com/ads-iframe-display.php?idzone=ZONE
  const src = `https://syndication.exoclick.com/ads-iframe-display.php?idzone=${encodeURIComponent(zoneId)}`;
  return (
    <iframe
      src={src}
      width={width}
      height={height}
      style={{ border: 0, display: 'block', margin: '0 auto' }}
      scrolling="no"
      title="Sponsored"
      sandbox="allow-scripts allow-popups allow-popups-to-escape-sandbox allow-same-origin"
      data-testid={testId}
    />
  );
}

function HouseUpsell({ variant }) {
  // House upsell that drives Free → Premium/Ultimate. Earns nothing per impression
  // but converts way better than network ads while the ExoClick account is pending.
  if (variant === 'native-card') {
    return (
      <Link
        to="/membership?plan=premium"
        className="group block relative aspect-[3/4] rounded-2xl overflow-hidden border border-pink-400/30 bg-gradient-to-br from-[#2A0A1F] via-[#4C1D95]/60 to-[#1B0E2E] hover:border-pink-400/60 transition-colors"
        data-testid="house-ad-native"
      >
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(244,114,182,0.35),transparent_55%)]" />
        <Sparkles className="absolute top-3 right-3 w-5 h-5 text-pink-200" />
        <div className="relative h-full flex flex-col justify-end p-3">
          <span className="text-[9px] font-bold uppercase tracking-widest text-white/40 mb-1">Sponsored</span>
          <h4 className="text-sm font-black text-white font-['Unbounded'] leading-tight mb-1">Go Ad-Free</h4>
          <p className="text-[11px] text-white/70 leading-snug mb-2">Premium removes ads, unlocks better AI &amp; longer memory.</p>
          <span className="inline-block px-3 py-1 rounded-full text-[10px] font-bold text-[#3F2A06] bg-gradient-to-r from-[#FCD34D] to-[#F59E0B] w-fit">
            From £9.99/mo →
          </span>
        </div>
      </Link>
    );
  }
  // sticky / banner variant
  return (
    <Link
      to="/membership?plan=premium"
      className="flex items-center justify-between gap-3 px-4 py-2.5 rounded-xl bg-gradient-to-r from-[#2A0A1F] via-[#3B0764] to-[#1B0E2E] border border-pink-400/30 hover:border-pink-400/60 transition-colors w-full"
      data-testid="house-ad-banner"
    >
      <div className="flex items-center gap-2.5 min-w-0">
        <Sparkles className="w-4 h-4 text-pink-300 flex-shrink-0" />
        <div className="min-w-0">
          <p className="text-xs font-bold text-white truncate">Tired of ads?</p>
          <p className="text-[10px] text-white/60 truncate">Premium is ad-free + smarter AI · From £9.99/mo</p>
        </div>
      </div>
      <span className="px-3 py-1 rounded-full text-[10px] font-black text-[#3F2A06] bg-gradient-to-r from-[#FCD34D] to-[#F59E0B] flex-shrink-0">
        Upgrade
      </span>
    </Link>
  );
}

export default function AdSlot({ variant = 'banner', className = '' }) {
  const { user } = useAuth();
  const [eligible, setEligible] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const registeredRef = useRef(false);

  useEffect(() => {
    if (isAdFreeTier(user?.subscription_tier)) {
      setEligible(false);
      return;
    }
    setEligible(canShowAd());
  }, [user?.subscription_tier]);

  useEffect(() => {
    // Register impression once when this slot becomes eligible
    if (eligible && !registeredRef.current) {
      registeredRef.current = true;
      registerImpression();
    }
  }, [eligible]);

  if (isAdFreeTier(user?.subscription_tier)) return null;
  if (!eligible || dismissed) return null;

  // ── Network ad (ExoClick) ─────────────────────────────────────────────
  const useNative = variant === 'native-card' && EXOCLICK_NATIVE_ZONE;
  const useBanner = (variant === 'sticky' || variant === 'banner') && EXOCLICK_BANNER_ZONE;

  if (variant === 'sticky') {
    return (
      <div
        className={`fixed bottom-16 lg:bottom-4 left-2 right-2 lg:left-1/2 lg:-translate-x-1/2 lg:w-[728px] z-40 ${className}`}
        data-testid="ad-slot-sticky"
      >
        <div className="relative">
          {useBanner ? (
            <div className="bg-black/60 backdrop-blur-md rounded-xl border border-white/10 p-1">
              <ExoClickIframe zoneId={EXOCLICK_BANNER_ZONE} width="100%" height="60" testId="exoclick-banner" />
            </div>
          ) : (
            <HouseUpsell variant="sticky" />
          )}
          <button
            onClick={() => setDismissed(true)}
            className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-black border border-white/20 text-white/70 hover:text-white flex items-center justify-center"
            aria-label="Dismiss ad"
            data-testid="ad-dismiss"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      </div>
    );
  }

  if (variant === 'native-card') {
    return useNative ? (
      <div className={`aspect-[3/4] rounded-2xl overflow-hidden border border-white/10 bg-black ${className}`} data-testid="ad-slot-native">
        <ExoClickIframe zoneId={EXOCLICK_NATIVE_ZONE} width="100%" height="100%" testId="exoclick-native" />
      </div>
    ) : (
      <div className={className} data-testid="ad-slot-native">
        <HouseUpsell variant="native-card" />
      </div>
    );
  }

  // default banner
  return (
    <div className={className} data-testid="ad-slot-banner">
      {useBanner ? (
        <ExoClickIframe zoneId={EXOCLICK_BANNER_ZONE} width="100%" height="90" testId="exoclick-banner-inline" />
      ) : (
        <HouseUpsell variant="banner" />
      )}
    </div>
  );
}
